import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Share2, Clock, User, Tag } from 'lucide-react';
import { toast } from 'react-toastify';
import { STORAGE_BASE } from '../config';
import Skeleton from '../components/Skeleton';
import Breadcrumb from '../components/Breadcrumb';
import { setJsonLd, siteOrigin } from '../components/SeoJsonLd';
import { CommentsBox } from '../components/CommentsBox';
import { RowView, applySeo } from './HomePage';

/** رندر ساده markdown (بدون وابستگی): تیترها، بولد، پاراگراف، لیست. */
function Md({ text }) {
  const lines = String(text || '').split('\n');
  const els = [];
  let list = [];
  const flush = () => { if (list.length) { els.push(<ul key={'u' + els.length} style={{ paddingRight: 20, lineHeight: 2.1, margin: '8px 0' }}>{list}</ul>); list = []; } };
  const inline = (s, k) => {
    const parts = String(s).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') && p.length > 4)
      ? <strong key={k + '-' + i}>{p.slice(2, -2)}</strong> : <span key={k + '-' + i}>{p}</span>);
  };
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (/^#{1,3}\s/.test(t)) { flush(); const lv = t.match(/^#+/)[0].length; els.push(React.createElement('h' + Math.min(lv + 1, 4), { key: 'h' + i, style: { fontWeight: 800, margin: '18px 0 8px', lineHeight: 1.8 } }, inline(t.replace(/^#+\s*/, ''), 'h' + i))); }
    else if (/^[-*•]\s/.test(t)) { list.push(<li key={'l' + i}>{inline(t.replace(/^[-*•]\s*/, ''), 'l' + i)}</li>); }
    else if (t) { flush(); els.push(<p key={'p' + i} style={{ lineHeight: 2.1, margin: '8px 0', color: 'var(--fg)' }}>{inline(t, 'p' + i)}</p>); }
  });
  flush();
  return <>{els}</>;
}

export default function PostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [siteName, setSiteName] = useState('');
  const [cats, setCats] = useState([]);
  const [tpl, setTpl] = useState([]);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [p, s] = await Promise.all([
          axios.get(`/posts/${slug}`),
          axios.get('/site-settings').catch(() => ({ data: {} })),
        ]);
        setPost(p.data);
        setSiteName(s.data?.site_name || s.data?.siteName || '');
        axios.get('/post-categories').then(r => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        axios.get('/posts', { params: { per_page: 4 } }).then(r => {
          const list = r.data?.data || r.data || [];
          setRelated((Array.isArray(list) ? list : []).filter(x => String(x.slug || x.id) !== String(slug)).slice(0, 3));
        }).catch(() => {});
        const pageSlug = s.data?.post_detail_page;
        if (pageSlug) {
          try {
            const vw = typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop';
            const pg = await axios.get(`/pages/${pageSlug}`);
            const rows = pg.data?.platforms?.[vw] || pg.data?.platforms?.desktop || [];
            setTpl(rows.filter(x => x.enabled !== false));
          } catch { setTpl([]); }
        } else setTpl([]);
        applySeo(p.data || {}, p.data?.title);
        setLoading(false);
      } catch (err) {
        setError(err.response?.status === 404 ? 'این مطلب یافت نشد.' : 'خطا در بارگذاری مطلب');
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return (
    <div style={{ width: '100%', padding: '24px clamp(10px, 3vw, 44px) 60px', direction: 'rtl' }}>
      <Skeleton height={34} width="65%" style={{ margin: '0 auto' }} />
      <Skeleton height={14} width={150} style={{ margin: '12px auto 0' }} />
      <Skeleton height={320} style={{ borderRadius: 16, marginTop: 16 }} />
      <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Skeleton height={16} /><Skeleton height={16} width="92%" /><Skeleton height={16} width="78%" /><Skeleton height={16} width="85%" />
      </div>
    </div>
  );
  if (error || !post) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center' }}>
        <div style={{ color: '#DC2626', marginBottom: 16 }}>{error || 'خطا'}</div>
        <Link to="/blog">بازگشت به وبلاگ</Link>
      </div>
    );
  }

  const ctx = { post };
  const hasCommentsWidget = (rows) => Array.isArray(rows) && (rows || []).some(r =>
    (r.columns || []).some(c => (c.widgets || []).some(w => w.type === 'post_comments' && w.enabled !== false)));
  const showDefaultComments = !hasCommentsWidget(post.rows) && !hasCommentsWidget(tpl);
  const share = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: post?.title, url: window.location.href }); return; }
      await navigator.clipboard.writeText(window.location.href);
      toast.success('لینک مطلب کپی شد.');
    } catch {}
  };
  const cover = post.cover ? (/^https?:\/\//.test(post.cover) ? post.cover : `${STORAGE_BASE}/${String(post.cover).replace(/^\/+/, '')}`) : null;
  const readMin = Math.max(1, Math.round((post.excerpt || post.body || '').replace(/<[^>]+>/g, '').length / 900));
  // GEO/AEO: BlogPosting + BreadcrumbList
  const ldPost = (() => {
    try {
      const origin = siteOrigin();
      const url = `${origin}/blog/${post.slug}`;
      const img = cover && !/^https?:\/\//.test(cover) ? origin + (cover.startsWith('/') ? cover : `/${cover}`) : cover;
      const text = String(post.excerpt || post.body || '').replace(/<[^>]+>/g, ' ').slice(0, 3000);
      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'BlogPosting', '@id': `${url}#post`, headline: post.title, url,
            ...(img ? { image: [img] } : {}),
            ...(text ? { description: text.slice(0, 500), articleBody: text } : {}),
            ...(post.published_at ? { datePublished: post.published_at } : {}),
            author: { '@type': 'Organization', name: siteName || ((typeof document !== 'undefined' && document.title) ? document.title : '') },
            publisher: { '@type': 'Organization', name: siteName || '' },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'وبلاگ', item: `${origin}/blog` },
              { '@type': 'ListItem', position: 2, name: post.title, item: url },
            ],
          },
        ],
      };
    } catch { return null; }
  })();
  setJsonLd(`post-${post.slug || post.id}`, ldPost);

  return (
    <div style={{ width: '100%', padding: '24px clamp(10px, 3vw, 44px) 60px', direction: 'rtl' }}>
      <Breadcrumb items={[{ label: 'وبلاگ', to: '/blog' }, ...(post.category ? [{ label: post.category?.name || post.category }] : []), { label: post.title }]} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        {post.category && (
          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--primary)', background: 'color-mix(in srgb, var(--primary) 12%, transparent)', borderRadius: 999, padding: '5px 14px' }}>{post.category?.name || post.category}</span>
        )}
        <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, var(--border), transparent)' }} />
      </div>
      <h1 className="serif" style={{ fontWeight: 800, fontSize: 'clamp(1.55rem, 4.5vw, 2.5rem)', lineHeight: 1.65, margin: '0 0 12px', letterSpacing: '-0.01em' }}>{post.title}</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--muted-fg)', marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><User size={14} /> {post.author || 'تحریریه'}</span>
        {post.published_at && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clock size={14} /> {new Date(post.published_at).toLocaleDateString('fa-IR')}</span>}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clock size={14} /> {Number(readMin).toLocaleString('fa-IR')} دقیقه مطالعه</span>
        {post.category && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Tag size={14} /> {post.category?.name || post.category}</span>}
        <span style={{ flexGrow: 1 }} />
        <button onClick={share} title="اشتراک‌گذاری" style={{ width: 34, height: 34, borderRadius: '50%', border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--muted-fg)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Share2 size={16} />
        </button>
      </div>
      {cover && <img src={cover} alt={post.title} style={{ width: '100%', maxHeight: 520, objectFit: 'cover', borderRadius: 'var(--shape-card)', display: 'block', background: 'var(--muted)', marginBottom: 22, boxShadow: '0 24px 60px -30px rgba(0,0,0,0.35)' }} />}
      {post.excerpt && (
        <div className="serif" style={{ fontSize: '1.06rem', lineHeight: 2.2, color: 'var(--fg)', background: 'color-mix(in srgb, var(--primary) 5%, var(--card))', borderRight: '4px solid var(--primary)', borderRadius: 'var(--shape-card)', padding: '18px 22px', marginBottom: 22, fontWeight: 500 }}>
          {post.excerpt}
        </div>
      )}
      <div className="blog-layout">
      <div className="post-body blog-main">
      {typeof post.rows === 'string' && post.rows.trim() !== '' ? (
        <Md text={post.rows} />
      ) : (post.rows || []).filter(r => r.enabled !== false).map((r, i) => (
        r.columns ? <RowView key={'p' + (r.id || i)} row={r} data={ctx} /> : null
      ))}
      {tpl.filter(r => r.enabled !== false).map((r, i) => (
        r.columns ? <RowView key={'t' + (r.id || i)} row={r} data={ctx} /> : null
      ))}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--shape-card)', padding: '18px 20px', marginTop: 26 }}>
        <span style={{ width: 46, height: 46, borderRadius: 999, background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)', display: 'inline-grid', placeItems: 'center', fontWeight: 900, fontSize: '1.1rem', flexShrink: 0 }}>{(post.author || 'ت').charAt(0)}</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{post.author || `تحریریه ${siteName || 'ما'}`}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--muted-fg)', lineHeight: 2, marginTop: 3 }}>منتشرکننده مطالب آموزشی {siteName ? `مجله ${siteName}` : 'وبلاگ'}.</div>
        </div>
      </div>
      {showDefaultComments && (
        <div style={{ marginTop: 28 }}>
          <CommentsBox type="post" id={post.id} />
        </div>
      )}
      </div>
      <aside className="blog-side">
        <div className="side-card">
          <div className="side-title">اشتراک‌گذاری</div>
          <button onClick={share} style={{ width: '100%', padding: '11px 16px', borderRadius: 'var(--shape-btn)', border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Share2 size={16} /> اشتراک‌گذاری مطلب
          </button>
        </div>
        {related.length > 0 && (
          <div className="side-card">
            <div className="side-title">مطالب مرتبط</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {related.slice(0, 5).map(r => (
                <Link key={r.id} to={`/blog/${r.slug || r.id}`} className="side-link">
                  <span style={{ lineHeight: 1.8 }}>{String(r.title).slice(0, 44)}{String(r.title).length > 44 ? '…' : ''}</span>
                  {r.published_at && <span style={{ color: 'var(--muted-fg)', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{new Date(r.published_at).toLocaleDateString('fa-IR')}</span>}
                </Link>
              ))}
            </div>
          </div>
        )}
        {cats.length > 0 && (
        <div className="side-card">
          <div className="side-title">موضوعات مجله</div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {cats.map(c => c.name).filter(Boolean).slice(0, 10).map(t => (
              <Link key={t} to={`/blog?q=${encodeURIComponent(t)}`} style={{ padding: '5px 11px', borderRadius: 999, border: '1px solid var(--border)', color: 'var(--fg)', fontSize: '0.74rem', fontWeight: 700, textDecoration: 'none' }}>#{t}</Link>
            ))}
          </div>
        </div>
        )}
        <div className="side-card" style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--primary) 16%, var(--card)), var(--card) 75%)' }}>
          <div className="side-title">بازگشت به وبلاگ</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--muted-fg)', lineHeight: 2, marginBottom: 10 }}>آموزش‌ها و راهنماهای بیشتر را در وبلاگ{siteName ? ` ${siteName}` : ''} ببین.</div>
          <Link to="/blog" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px 16px', borderRadius: 'var(--shape-btn)', border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontWeight: 800, cursor: 'pointer', textDecoration: 'none', fontSize: '0.86rem' }}>
            رفتن به وبلاگ
          </Link>
        </div>
      </aside>
      </div>
      {related.length > 0 && (
        <div style={{ marginTop: 38 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <h2 className="serif" style={{ fontWeight: 800, fontSize: '1.25rem', margin: 0 }}>مطالب مرتبط</h2>
            <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <Link to="/blog" style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' }}>همه مطالب ←</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {related.map(r => {
              const src = r.cover ? (/^https?:\/\//.test(r.cover) ? r.cover : `${STORAGE_BASE}/${String(r.cover).replace(/^\/+/, '')}`) : null;
              return (
                <Link key={r.id} to={`/blog/${r.slug || r.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="pcard-shell" style={{ border: '1px solid var(--border)', borderRadius: 'var(--shape-card)', overflow: 'hidden', background: 'var(--card)', height: '100%' }}>
                    {src && <img src={src} alt={r.title} loading="lazy" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block', background: 'var(--muted)' }} />}
                    <div style={{ padding: 12 }}>
                      <div style={{ fontSize: '0.86rem', fontWeight: 700, lineHeight: 1.8 }}>{r.title}</div>
                      {r.published_at && <div style={{ fontSize: '0.72rem', color: 'var(--muted-fg)', marginTop: 4 }}>{new Date(r.published_at).toLocaleDateString('fa-IR')}</div>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
